import React, { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ErrorOverlay } from '@ff-os/shared-react/components/error-overlay/ErrorOverlay';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { Heading } from '@ff-os/shared-react/components/panel/Heading';
import { Panel } from '@ff-os/shared-react/components/panel/Panel';
import { selectHasErrorByGroupId, selectPhotoIdsByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import classNames from 'classnames';
import { selectActiveBorderOptionForVariantId } from '../../../_redux-borders';
import { selectAmountInQueueByGroupId } from '../../../_redux-groups';
import { selectActivePaperOptionForVariantId } from '../../../_redux-papers';
import { selectActiveSizeOptionForVariantId } from '../../../_redux-sizes';
import { VariantOption } from '../../../VariantOption';
import { TruncatedPhotoPrintsList } from './TruncatedPhotoPrintsList';
import styles from './PhotoPrintsGroupPreview.module.scss';

const _NotEmptyPhotoPrintsGroupPreview = ({
    setDetailWindowOpen, photoIds, size, paper, border, className,
    failedUploadsCount, successfulUploadsCount,
    amountInQueue, hasError, browse
}: Props) => {

    const { t } = useTranslation();

    const addMore: MouseEventHandler = (event) => {
        // prevent detail window from opening
        event.stopPropagation();
        browse();
    };

    return (
        <Panel className={classNames(className, styles.container)} onClick={() => setDetailWindowOpen(true)}>
            <Heading icon='shopping-cart' className={styles.heading}>
                {t('photo_prints.group_name', {
                    size: t(size.label!.key, size.label!.args),
                    paper: t(paper.name.key, paper.name.args),
                    border: border ? t(border.name.key, border.name.args) : ''
                })}
            </Heading>

            <div className={styles.listContainer}>

                {hasError && (
                    <ErrorOverlay />
                )}

                <TruncatedPhotoPrintsList
                    photoIds={photoIds}
                    amountInQueue={amountInQueue}
                    maxPreviewCount={2} />

            </div>

            <Grid gap='xs' wrap>
                <Cell className={styles.buttonCell}>
                    <div className={classNames(styles.button, styles.addMoreButton)}
                        onClick={addMore}>
                        <Icon size='small' type='image-file-upload' className={styles.icon} />
                        {t('photo_prints.add_more')}
                    </div>
                </Cell>

                <Cell className={styles.buttonCell}>
                    <div className={classNames(styles.button, styles.editButton)}>
                        {t('commons.edit')}
                    </div>
                </Cell>
            </Grid>

            <div className={styles.message}>
                {failedUploadsCount > 0 && (
                    <>{t('photo_prints.photos_errored', { count: failedUploadsCount })} </>
                )}

                {successfulUploadsCount > 0 && (
                    <>{t('photo_prints.photos_uploaded', { count: successfulUploadsCount })}</>
                )}
            </div>
        </Panel>
    );
};

type OwnProps = {
    browse: () => void;
    groupId: string;
    variantId: string;
    setDetailWindowOpen: (open: boolean) => void;
    className?: string;
    successfulUploadsCount: number;
    failedUploadsCount: number;
};

type Props = OwnProps & {
    paper: VariantOption;
    size: VariantOption;
    border?: VariantOption;
    photoIds: string[];
    amountInQueue: number;
    hasError: boolean;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId)!,
    amountInQueue: selectAmountInQueueByGroupId(state, ownProps.groupId)!,
    size: selectActiveSizeOptionForVariantId(state, ownProps.variantId)!,
    paper: selectActivePaperOptionForVariantId(state, ownProps.variantId)!,
    border: selectActiveBorderOptionForVariantId(state, ownProps.variantId),
    hasError: selectHasErrorByGroupId(state, ownProps.groupId)
});

export const NotEmptyPhotoPrintsGroupPreview = connect(
    mapStateToProps
)(_NotEmptyPhotoPrintsGroupPreview);